import _ from 'lodash';
import moment from 'moment';
import { Component, ReactElement } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from '../../../../shared/types/common';
import { createActionSetError, TError } from '../../../../../../shared/frontend/scripts/actions/errorActions';
import { Spinner } from '../../../../../../shared/frontend/components/presentational/spinner';
import WorldFirstTimeline from '../../components/presentational/worldFirstTimeline';
import frontendLog from '../../libs/frontendLog';
import { TApplicationState } from '../../libs/store';
import { areFieldsDifferent } from '../../../../../../shared/frontend/libs/utils';
import apiService from '../../services/apiService';
import RaceVisualizerActions from '../../actions/raceVisualizerActions';
import { t } from 'i18next';

const REFRESH_TIMEOUT_MS = 53 * 1000;

type TPropsFromState = any;

type TPropsThatDispatch = {
  setWorldFirstTracker(instanceId: string, actionData: any): void;
  showLoadingSpinner(instanceId: string, show: boolean): void;
  forceRefresh(instanceId: string): void;
  showError(actionData: TError): void;
};

export type TRaidWorldFirstTrackerProps = TPropsFromState & TPropsThatDispatch & RouteComponentProps<any, any>;

function sortTimelines(a: any, b: any) {
  const aGuilds = a.timeline.filter((aa: any) => aa.totalGuilds > 0);
  const aMaxProgress = aGuilds.length > 0 ? _.last<any>(aGuilds).progress : 0;
  const bGuilds = b.timeline.filter((bb: any) => bb.totalGuilds > 0);
  const bMaxProgress = bGuilds.length > 0 ? _.last<any>(bGuilds).progress : 0;
  if (aMaxProgress === bMaxProgress) {
    if (aMaxProgress === 0) {
      return 0;
    }
    const aTopGuild = _.last<any>(aGuilds).guilds[0];
    const bTopGuild = _.last<any>(bGuilds).guilds[0];
    if (!aTopGuild || !bTopGuild) {
      // FIXME: shouldn't happen?
      return 0;
    }
    return moment(aTopGuild.defeatedAt).diff(bTopGuild.defeatedAt);
  }
  return bMaxProgress - aMaxProgress;
}

function RaidWorldFirstTrackerContent(props: any) {
  if (!props.timelines) {
    return (
      <div>
        <div
          className="slds-p-around--xx-large"
          style={{ backgroundColor: '#151314', position: 'relative', height: '170px' }}
        >
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div>
      {props.timelines.sort(sortTimelines).map((tl: any, index: number) => (
        <WorldFirstTimeline
          key={tl.region.slug}
          ordinal={index}
          region={tl.region}
          difficulty="mythic"
          raid={props.raid}
          timeline={tl.timeline}
          forceBackgroundUrl={`/images/${props.raid.slug}/rwf_coverage_tracker.jpg`}
          showWorldFirstRegionTracker={true}
          forceTitle={`${t(`raids:${props.raid.slug}.short-name`)} R•W•F Tracker`}
        />
      ))}
    </div>
  );
}

/**
 * RaceVisualizerBase
 */
class RaceVisualizerBase extends Component<TRaidWorldFirstTrackerProps, {}> {
  // public static async serverPreloadActions(params: any) {
  //   const { match } = params;
  //   const loadParams: any = {
  //     raid: match.raid,
  //     difficulty: 'mythic'
  //   };

  //   return new Promise((resolve: any, reject: any) => {
  //     apiService.getRaidRaceTracker(loadParams, (err: any, data: any) => {
  //       if (err) {
  //         reject(err);
  //       } else {
  //         resolve([WorldFirstTrackerActions.setWorldFirstTracker(data.worldFirstTracker)]);
  //       }
  //     });
  //   });
  // }

  private interval: any = null;

  public componentDidMount() {
    if (this.interval) {
      clearInterval(this.interval);
      this.interval = null;
    }
    if (this.props.refresh !== false) {
      this.interval = setInterval(() => this.props.forceRefresh(), REFRESH_TIMEOUT_MS);
    }
  }

  public componentWillUnmount() {
    clearInterval(this.interval);
    this.interval = null;
  }

  public render(): ReactElement<TRaidWorldFirstTrackerProps> {
    return (
      <div className={this.props.className} style={this.props.style}>
        <RaidWorldFirstTrackerContent {...this.props} />
      </div>
    );
  }
}

// take state from store and convert into props
function mapStateToProps(state: TApplicationState, ownProps: any): TPropsFromState {
  return {
    ...state.raceVisualizer.instances[ownProps.instanceId || 'default'],
    siteMeta: state.siteMeta,
    siteConfig: state.siteConfig,
    currentFeatures: state.currentFeatures
  };
}

// return props that need access to `dispatch` function (props that can modify the store?)
function mapDispatchToProps(dispatch: Function): TPropsThatDispatch {
  return {
    setWorldFirstTracker(instanceId: string, rankings: any) {
      dispatch(RaceVisualizerActions.setWorldFirstTracker(instanceId, rankings));
    },
    showLoadingSpinner(instanceId: string, show: boolean) {
      dispatch(RaceVisualizerActions.showLoadingSpinner(instanceId, show));
    },
    forceRefresh(instanceId: string) {
      dispatch(RaceVisualizerActions.forceRefresh(instanceId));
    },
    showError(actionData: TError) {
      dispatch(createActionSetError(actionData));
    }
  };
}

// return one entire set of props
function mergeProps(stateProps: TPropsFromState, dispatchProps: TPropsThatDispatch, ownProps: any) {
  const loadParams: any = {
    raid: ownProps.raid,
    region: ownProps.region,
    difficulty: 'mythic'
  };

  const instanceId = ownProps.instanceId || 'default';
  const checkFields = ['raid', 'difficulty', 'region'];
  const contentChanged =
    stateProps.forceRefresh || !stateProps.ui || areFieldsDifferent(checkFields, loadParams, stateProps.ui);
  if (!stateProps.isLoading && contentChanged) {
    dispatchProps.showLoadingSpinner(instanceId, true);

    apiService.getRaidRaceTracker(loadParams, (err: any, result?: any) => {
      if (err) {
        frontendLog.error({ err }, 'getRaidRaceTracker failed');
        dispatchProps.showLoadingSpinner(instanceId, false);
        return;
      }

      if (!result) {
        frontendLog.error('getRaidRaceTracker got empty result');
        dispatchProps.showLoadingSpinner(instanceId, false);
        return;
      }

      dispatchProps.setWorldFirstTracker(instanceId, result.worldFirstTracker);
    });
  }

  return {
    ...ownProps,
    ...stateProps,
    ...dispatchProps
  };
}

export default connect<TPropsFromState, TPropsThatDispatch, TRaidWorldFirstTrackerProps>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(RaceVisualizerBase as any);
